import { mapBracketsToStandings } from "./helpers/mapBracketsToStandings";
import { useMemo, useState } from "react";
import { formatBountyValue } from "./helpers/format";
import { useEffect } from "react";
import { Bracket } from "@portal-frontend-ssr/blast-api";
import { TournamentTeamSlot } from "@portal-frontend-ssr/blast-api";
import { motion } from "motion/react";
import { generateTeamLogoSrc } from "~/shared/helpers/url/generateTeamLogoSrc";

export const BountyStandings = ({
  brackets,
  tournamentTeamSlots,
}: {
  brackets: Bracket[];
  tournamentTeamSlots: TournamentTeamSlot[];
}) => {
  const [showAll, setShowAll] = useState(false);

  const isBountyTournament = useMemo(() => {
    return brackets?.some((bracket) => bracket.format.includes("bounty"));
  }, [brackets]);

  const standings = useMemo(() => {
    if (!brackets || !tournamentTeamSlots) return [];
    return mapBracketsToStandings(brackets, tournamentTeamSlots);
  }, [brackets, tournamentTeamSlots]);

  const eliminatedStandingsWithBanked = standings.filter((standing) => standing.eliminated && standing.banked > 0);
  const eliminatedStandingsWithoutBanked = standings.filter((standing) => standing.eliminated && standing.banked === 0);
  const weHaveAWinner = standings.filter((standing) => !standing.eliminated).length === 1;

  useEffect(() => {
    if (weHaveAWinner) {
      setShowAll(true);
    }
  }, [weHaveAWinner]);

  if (!isBountyTournament) return null;

  return (
    <div className="flex w-full flex-col gap-5">
      <div className="flex gap-2.5 font-style-label-l3">
        <span>{weHaveAWinner ? "Final Bounties" : "Current Bounties"}</span>
        {!weHaveAWinner && (
          <button className="button-tertiary" onClick={() => setShowAll(!showAll)}>
            {showAll ? "See Less" : "See All"}
          </button>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <div className="flex flex-row flex-wrap gap-1">
          {standings
            .filter((standing) => !standing.eliminated)
            .map((standing) => (
              <div
                key={standing.id}
                className="flex min-w-32 flex-1 grow flex-col items-center justify-center gap-2 rounded-small bg-background-95 p-3"
              >
                {weHaveAWinner ? (
                  <span className="text-neutral font-style-label-l4">Winner</span>
                ) : (
                  <span className="text-neutral font-style-label-l4">Seed #{standing.bountySeed}</span>
                )}

                <img src={generateTeamLogoSrc({ id: standing.id ?? "" })} alt={standing.name} className="size-6" />
                {!weHaveAWinner && (
                  <span className="font-style-body-b2">
                    {formatBountyValue(standing.bountyValue, { notation: "standard" })}
                  </span>
                )}
                <span className="text-neutral font-style-body-b3">
                  {formatBountyValue(standing.banked, { notation: "standard" })} Banked
                </span>
              </div>
            ))}
        </div>
        {showAll && (
          <>
            <hr className="my-3 w-full text-background-80" />
            <motion.div
              className="flex flex-row flex-wrap gap-1"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              {eliminatedStandingsWithBanked.map((standing) => (
                <div
                  key={standing.id}
                  className="flex min-w-32 flex-1 grow flex-col items-center justify-center gap-2 rounded-small border border-background-80 bg-transparent p-3"
                >
                  {!weHaveAWinner && <span className="text-neutral font-style-label-l4">No Seed</span>}
                  <img src={generateTeamLogoSrc({ id: standing.id ?? "" })} alt={standing.name} className="size-6" />
                  <div className="flex flex-col items-center justify-center gap-1 font-style-body-b3">
                    <span>{formatBountyValue(standing.banked, { notation: "standard" })}</span>
                    <span className="text-neutral font-style-body-b3">Banked</span>
                  </div>
                </div>
              ))}
              <div className="flex w-full min-w-48 flex-1 shrink-0 grow basis-2/3 flex-col items-center justify-center gap-2 rounded-small border border-background-80 bg-transparent p-3">
                {!weHaveAWinner && <span className="text-neutral font-style-label-l4">No Seed</span>}
                <div className="flex flex-row flex-wrap items-center justify-center gap-1">
                  {eliminatedStandingsWithoutBanked.map((standing) => (
                    <img
                      key={standing.id}
                      src={generateTeamLogoSrc({ id: standing.id ?? "" })}
                      alt={standing.name}
                      className="size-6"
                    />
                  ))}
                </div>
                <span className="pt-1 text-neutral font-style-body-b3">$0 Banked</span>
              </div>
            </motion.div>
          </>
        )}
      </div>
    </div>
  );
};
